/* eslint-disable max-lines */
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import StepShell from '../StepShell';
import { colors, layout } from '../styles';
import StateService from '../../../../../services/State';
import usePreEmploymentDraft from './preEmployment/usePreEmploymentDraft';
import DateInput from '../components/DateInput';
import { formatPhone, formatSsn } from '../utils/numberValidation';
import { BaseStepProps } from './BaseStepProps';

const PreEmploymentPersonalStep = ({
  step,
  stepIndex,
  totalSteps,
  completedSteps,
  token,
  tokenData,
  applicationDraft,
  onBack,
  onPrevious,
  onNext,
  onSaveLater,
}: BaseStepProps) => {
  const { formData, updateForm, saveAsDraft } = usePreEmploymentDraft(token, applicationDraft);
  const [states, setStates] = useState<{ uf: string; name: string }[]>([]);
  const [saving, setSaving] = useState(false);

  const handleSaveLater = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await saveAsDraft({ token, userId: tokenData.userId, userName: tokenData.userName });
      toast.success('Progress saved.');
      onSaveLater();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Could not save progress.');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    StateService('')
      .listSelect()
      .then((result: any) => setStates(result?.data || []))
      .catch(() => setStates([]));
  }, []);

  const canSubmit =
    !!formData.legalFirstName &&
    !!formData.legalLastName &&
    !!formData.address &&
    !!formData.city &&
    !!formData.state &&
    !!formData.zipCode &&
    !!formData.otherStatesLived &&
    !!formData.phoneNumber &&
    !!formData.email &&
    !!formData.dob;

  return (
    <StepShell
      stepNumber={stepIndex + 1}
      totalSteps={totalSteps}
      completedSteps={completedSteps}
      title={step.title}
      onBack={onBack}
      onPrevious={onPrevious}
      onSaveLater={handleSaveLater}
      onNext={onNext}
      nextLabel="Next"
      nextDisabled={!canSubmit}
    >
      <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Personal Information</h3>
      <p style={{ color: colors.muted, fontSize: 14, marginTop: 6 }}>
        Please provide your basic personal information as it appears on official documents.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={layout.label}>Legal First Name *</label>
          <input
            type="text"
            maxLength={40}
            value={formData.legalFirstName}
            onChange={(e) => updateForm({ legalFirstName: e.target.value })}
            style={layout.input}
          />
        </div>
        <div>
          <label style={layout.label}>Maiden Name (if any)</label>
          <input
            type="text"
            maxLength={40}
            value={formData.maidenName}
            onChange={(e) => updateForm({ maidenName: e.target.value })}
            style={layout.input}
          />
        </div>
        <div>
          <label style={layout.label}>Legal Last Name *</label>
          <input
            type="text"
            maxLength={40}
            value={formData.legalLastName}
            onChange={(e) => updateForm({ legalLastName: e.target.value })}
            style={layout.input}
          />
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={layout.label}>Street Address *</label>
        <input
          type="text"
          maxLength={130}
          value={formData.address}
          onChange={(e) => updateForm({ address: e.target.value })}
          style={layout.input}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={layout.label}>City *</label>
          <input
            type="text"
            value={formData.city}
            onChange={(e) => updateForm({ city: e.target.value })}
            style={layout.input}
          />
        </div>
        <div>
          <label style={layout.label}>County</label>
          <input
            type="text"
            value={formData.county}
            onChange={(e) => updateForm({ county: e.target.value })}
            style={layout.input}
          />
        </div>
        <div>
          <label style={layout.label}>State *</label>
          <select value={formData.state} onChange={(e) => updateForm({ state: e.target.value })} style={layout.select}>
            <option value="">Select...</option>
            {states.map((s) => (
              <option key={s.uf} value={s.uf}>
                {s.uf}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={layout.label}>ZIP Code *</label>
          <input
            type="text"
            value={formData.zipCode}
            onChange={(e) => updateForm({ zipCode: e.target.value })}
            style={layout.input}
          />
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={layout.label}>Other states you have lived in (last 7 years) *</label>
        <input
          type="text"
          value={formData.otherStatesLived}
          onChange={(e) => updateForm({ otherStatesLived: e.target.value })}
          placeholder="Enter a state or 'NA' if none"
          style={layout.input}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={layout.label}>Phone Number *</label>
          <input
            type="tel"
            value={formData.phoneNumber}
            onChange={(e) => updateForm({ phoneNumber: formatPhone(e.target.value) })}
            placeholder="(555) 123-4567"
            style={layout.input}
          />
        </div>
        <div>
          <label style={layout.label}>Email Address *</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => updateForm({ email: e.target.value })}
            style={layout.input}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={layout.label}>Date of Birth *</label>
          <DateInput value={formData.dob} onChange={(v) => updateForm({ dob: v })} />
        </div>
        <div>
          <label style={layout.label}>Social Security Number</label>
          <input
            type="text"
            value={formData.ssn}
            onChange={(e) => updateForm({ ssn: formatSsn(e.target.value) })}
            placeholder="XXX-XX-XXXX"
            style={layout.input}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={layout.label}>Shirt Size</label>
          <select
            value={formData.shirtSize}
            onChange={(e) => updateForm({ shirtSize: e.target.value })}
            style={layout.select}
          >
            <option value="">Select...</option>
            <option value="XS">XS</option>
            <option value="S">S</option>
            <option value="M">M</option>
            <option value="L">L</option>
            <option value="XL">XL</option>
            <option value="XXL">XXL</option>
          </select>
        </div>
        <div>
          <label style={layout.label}>Allergies (food, environmental, etc.)</label>
          <input
            type="text"
            value={formData.allergies}
            onChange={(e) => updateForm({ allergies: e.target.value })}
            style={layout.input}
          />
        </div>
      </div>
    </StepShell>
  );
};

export default PreEmploymentPersonalStep;
